/**
 * Session timeout & auto-logout — Auto-Gère.
 *
 * Trois mesures combinées pour s'assurer que l'utilisateur est bien
 * déconnecté quand il « part » :
 *
 *   1. INACTIVITÉ (10 min) — Aucune souris/clavier/touche pendant 10 min ⇒ logout.
 *      Couvre le cas « l'utilisateur a laissé l'onglet ouvert et est parti dîner ».
 *
 *   2. ONGLET CACHÉ (10 min) — Safety net : si l'onglet est en arrière-plan /
 *      l'app PWA est en background depuis >10 min, on déconnecte au retour.
 *      NB : ne délogue PAS sur un simple Alt+Tab ou changement de fenêtre —
 *      tant que la fenêtre reste OUVERTE, le profil reste OUVERT.
 *
 *   3. FERMETURE DU NAVIGATEUR — Au `pagehide`/`beforeunload`, on inscrit le
 *      timestamp dans localStorage. Au prochain démarrage, si l'écart entre
 *      « dernière fermeture » et « maintenant » dépasse 5 min, on force le
 *      logout — même si Chrome a restauré la sessionStorage via « Continuer
 *      où vous en étiez ». Couvre le cas « fermeture + réouverture du navigateur ».
 *
 * Les 3 mesures partagent la même API : on enregistre un callback `onTimeout`
 * (qui doit faire le logout) puis on appelle `start()` / `stop()`.
 */

import { clearToken } from './authStorage';

// Délais (en millisecondes)
export const INACTIVITY_LIMIT_MS    = 10 * 60 * 1000;   // 10 min sans action ⇒ logout
export const HIDDEN_LIMIT_MS        = 10 * 60 * 1000;   // 10 min en arrière-plan ⇒ logout (aligné inactivité)
export const UNLOAD_GAP_LIMIT_MS    = 5  * 60 * 1000;   // 5 min entre fermeture et réouverture
const CHECK_INTERVAL_MS             = 30 * 1000;        // poll toutes les 30 s

// Heartbeat — anti « Continuer où vous en étiez » de Chrome.
// Chrome (et certains autres navigateurs) restaurent les cookies de session
// quand l'utilisateur a activé la restauration d'onglets. On contourne ce
// comportement en écrivant régulièrement un timestamp dans localStorage
// pendant que l'app est visible. Au prochain démarrage, si le timestamp est
// périmé (> HEARTBEAT_STALE_MS), on suppose que le navigateur a été fermé
// et on force la déconnexion — même si le cookie est encore valide.
const HEARTBEAT_KEY            = 'auth_heartbeat_at';
const HEARTBEAT_WRITE_MS       = 10 * 1000;     // écrit toutes les 10 s
export const HEARTBEAT_STALE_MS = 30 * 1000;    // > 30 s ⇒ session considérée fermée

const LAST_ACTIVITY_KEY = 'auth_last_activity_at';
const LAST_UNLOAD_KEY   = 'auth_last_unload_at';
const HIDDEN_AT_KEY     = 'auth_hidden_at';

const safeNow = () => Date.now();
const lsSet = (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* quota / privé */ } };
const lsGet = (k)    => { try { return localStorage.getItem(k); } catch { return null; } };
const lsDel = (k)    => { try { localStorage.removeItem(k); } catch { /* */ } };

/**
 * Indique si une activité (heartbeat) a été observée récemment.
 *
 * - `true`  → l'app était active il y a < HEARTBEAT_STALE_MS → on peut faire
 *             confiance au cookie httpOnly et restaurer la session.
 * - `false` → pas d'activité récente → le cookie a probablement été restauré
 *             par le navigateur (Chrome « Continuer où vous en étiez ») après
 *             une fermeture ⇒ on force le logout côté client.
 *
 * Détecte aussi les reload de page (Performance API : type === 'reload') pour
 * lesquels on doit TOUJOURS considérer la session fraîche (sinon F5 délogue).
 */
export const isSessionFresh = () => {
  if (typeof window === 'undefined') return true;
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'reload') return true;
  } catch { /* ignore */ }
  const last = parseInt(lsGet(HEARTBEAT_KEY) || '0', 10);
  if (!last) return false;
  return (safeNow() - last) < HEARTBEAT_STALE_MS;
};

/**
 * Démarre la boucle d'écriture du heartbeat. À appeler après login réussi.
 * Retourne une fonction d'arrêt. Idempotent côté contrat ; un appel suivi
 * d'un autre sans stop() peut créer plusieurs intervals — l'appelant gère
 * la cleanup via le retour.
 */
export const startHeartbeat = () => {
  if (typeof window === 'undefined') return () => {};
  // Écriture immédiate pour qu'un reload juste après login soit considéré frais.
  lsSet(HEARTBEAT_KEY, safeNow());
  const id = setInterval(() => {
    // On n'écrit que si l'onglet est visible — sinon Chrome continuerait à
    // « rafraîchir » la session d'un onglet caché que l'utilisateur a oublié.
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      lsSet(HEARTBEAT_KEY, safeNow());
    }
  }, HEARTBEAT_WRITE_MS);
  return () => clearInterval(id);
};

/**
 * Purge le heartbeat (utilisé sur logout explicite pour s'assurer qu'un
 * éventuel reload immédiat post-logout ne soit pas considéré comme une
 * session vivante).
 */
export const clearHeartbeat = () => {
  lsDel(HEARTBEAT_KEY);
};

/**
 * Vérifie au démarrage de l'app si une déconnexion doit avoir lieu à cause
 * d'un long écart entre la dernière fermeture et le démarrage actuel.
 * À appeler UNE SEULE FOIS avant de lire le token initial.
 *
 * Depuis la migration auth v1.02 (cookie httpOnly), on doit AUSSI appeler
 * `/api/auth/logout` côté serveur — le `clearToken()` local ne purge plus
 * que le token in-memory, pas le cookie. On utilise `navigator.sendBeacon`
 * (fire-and-forget, ne bloque pas le démarrage) avec fallback `fetch keepalive`.
 *
 * @returns {boolean} true si un logout forcé a été appliqué.
 */
export const enforceUnloadGap = () => {
  const last = parseInt(lsGet(LAST_UNLOAD_KEY) || '0', 10);
  if (!last || (safeNow() - last) <= UNLOAD_GAP_LIMIT_MS) {
    return false;
  }
  // Purge le cookie httpOnly côté serveur (sinon /auth/me restaurerait la
  // session au montage de l'app, court-circuitant tout l'effort).
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    if (backend) {
      const url = `${backend}/api/auth/logout`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url);
      } else if (typeof fetch !== 'undefined') {
        fetch(url, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
      }
    }
  } catch { /* ignore — best effort */ }
  clearToken();
  lsDel(LAST_UNLOAD_KEY);
  lsDel(LAST_ACTIVITY_KEY);
  lsDel(HIDDEN_AT_KEY);
  clearHeartbeat();
  return true;
};

/**
 * Démarre les 3 surveillances (inactivité, hidden, unload).
 * Retourne une fonction de cleanup à appeler à la déconnexion / unmount.
 *
 * @param {() => void} onTimeout - Callback appelé quand la session expire.
 */
export const startSessionTimeouts = (onTimeout) => {
  if (typeof window === 'undefined') return () => {};

  lsSet(LAST_ACTIVITY_KEY, safeNow());

  const bumpActivity = () => lsSet(LAST_ACTIVITY_KEY, safeNow());

  // 1) Inactivité : on note la dernière action utilisateur.
  const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel', 'scroll'];
  // Throttle simple : ne pas écrire à chaque pixel de scroll.
  let lastWrite = 0;
  const onActivity = () => {
    const now = safeNow();
    if (now - lastWrite > 5000) {  // 1 écriture max toutes les 5 s
      lastWrite = now;
      bumpActivity();
    }
  };
  ACTIVITY_EVENTS.forEach((evt) => {
    window.addEventListener(evt, onActivity, { passive: true });
  });

  // 2) Onglet caché / app en arrière-plan
  //
  // Depuis correctif Feb 2026 : on ne délogue PLUS agressivement quand la
  // fenêtre passe en arrière-plan (Alt+Tab, changement d'onglet, PWA
  // minimisée). Tant que la fenêtre reste OUVERTE, le profil reste ouvert.
  //
  // Le logout n'a lieu que :
  //   - Sur inactivité > 10 min (poll `INACTIVITY_LIMIT_MS`)
  //   - Sur détection de fermeture réelle du navigateur au démarrage
  //     (`enforceUnloadGap()` + `isSessionFresh()`)
  //   - Sur onglet caché depuis > 10 min (safety net, `HIDDEN_LIMIT_MS`)
  //
  // Le heartbeat écrit dans localStorage sert de "ping vivant" détectable
  // au prochain démarrage — SI le processus a été vraiment tué (PWA fermée
  // par swipe, browser quit) le heartbeat devient stale > 30 s et
  // `isSessionFresh()` retournera false au remount.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      lsSet(HIDDEN_AT_KEY, safeNow());
    } else if (document.visibilityState === 'visible') {
      const hiddenAt = parseInt(lsGet(HIDDEN_AT_KEY) || '0', 10);
      lsDel(HIDDEN_AT_KEY);

      // Safety net : caché depuis très longtemps ⇒ logout (peut arriver si
      // l'utilisateur a laissé un onglet dormant plusieurs heures).
      if (hiddenAt && (safeNow() - hiddenAt) > HIDDEN_LIMIT_MS) {
        triggerLogout('hidden_too_long');
        return;
      }

      // Retour rapide : on rafraîchit l'activité pour repartir sur 10 min pleins.
      bumpActivity();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // 3) Fermeture du navigateur / onglet : on inscrit le timestamp pour
  //    le check effectué par `enforceUnloadGap()` à la prochaine ouverture.
  const onUnload = () => {
    try { localStorage.setItem(LAST_UNLOAD_KEY, String(safeNow())); } catch { /* */ }
  };
  window.addEventListener('pagehide', onUnload);
  window.addEventListener('beforeunload', onUnload);

  // Poll : vérifie l'inactivité régulièrement
  let stopped = false;
  let logoutFired = false;
  const triggerLogout = (reason) => {
    if (stopped || logoutFired) return;
    logoutFired = true;
    console.warn(`[SessionTimeout] Auto-logout : ${reason}`);
    lsDel(LAST_ACTIVITY_KEY);
    lsDel(LAST_UNLOAD_KEY);
    lsDel(HIDDEN_AT_KEY);
    try { onTimeout && onTimeout(); } catch { /* */ }
  };

  const interval = setInterval(() => {
    if (stopped) return;
    const last = parseInt(lsGet(LAST_ACTIVITY_KEY) || '0', 10);
    if (last && (safeNow() - last) > INACTIVITY_LIMIT_MS) {
      triggerLogout('inactivity_10min');
    }
  }, CHECK_INTERVAL_MS);

  return () => {
    stopped = true;
    clearInterval(interval);
    ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onUnload);
    window.removeEventListener('beforeunload', onUnload);
  };
};

/** Nettoie les artefacts localStorage liés à la session (appelé sur logout). */
export const clearSessionArtifacts = () => {
  lsDel(LAST_ACTIVITY_KEY);
  lsDel(LAST_UNLOAD_KEY);
  lsDel(HIDDEN_AT_KEY);
};
